
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import { cn } from "@/utils/utils";

export default function CategorizingWordsExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,   
  onComplete = () => {},
}){
  const isEditMode = typeof onUpdate === "function";

  /* ================= RAW DATA ================= */
  const rawCategories = Array.isArray(exercise.categories)
    ? exercise.categories
    : [];

  const rawWords = Array.isArray(exercise.words)
    ? exercise.words
    : [];

  /* ================= SAFE DATA ================= */
  const categories = isEditMode
    ? rawCategories
    : rawCategories.filter(Boolean);

  const words = isEditMode
    ? rawWords
    : rawWords.filter(
        w => w?.word?.trim() && w?.category?.trim()
      );

  /* ================= STUDENT STATE ================= */
  const [wordBank, setWordBank] = useState([]);
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);

  /* ================= INIT WORD BANK ================= */
  useEffect(() => {
    if (!isPreview) return;

    const shuffled = [...words.map(w => w.word)].sort(
      () => Math.random() - 0.5
    );
    setWordBank(shuffled);
  }, [isPreview, words]);

  /* ================= HELPERS ================= */
  const updateField = (field, value) => {
    if (isEditMode) onUpdate(field, value);
  };

  /* ================= DROP HANDLER ================= */
const handleDrop = (e, category) => {
  if (!isPreview || showResults) return;
    e.preventDefault();

    const word = e.dataTransfer.getData("text/plain");
    if (!word || !wordBank.includes(word)) return;

    const newAnswers = { ...answers, [word]: category };
    const newBank = wordBank.filter(w => w !== word);

    setAnswers(newAnswers);
    setWordBank(newBank);

    if (newBank.length === 0) checkAnswers(newAnswers);
  };

  const checkAnswers = finalAnswers => {
    if (completedRef.current) return;
    completedRef.current = true;

    const allCorrect = words.every(
      w =>
        finalAnswers[w.word]?.toLowerCase().trim() ===
        w.category.toLowerCase().trim()
    );

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-10">
        {/* Categories */}
        <div className="space-y-3">
          <p className="font-medium text-sm">Categories</p>

          {categories.map((cat, i) => (
            <div key={i} className="flex gap-3">
              <StringField
                value={cat}
                placeholder="e.g. Fruits"
                onChange={v => {
                  const next = [...categories];
                  next[i] = v;
                  updateField("categories", next);
                }}
              />
              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateField(
                    "categories",
                    categories.filter((_, idx) => idx !== i)
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}

          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              updateField("categories", [...categories, ""])
            }
          >
            Add Category
          </Button>
        </div>

        {/* Words */}
        <div className="space-y-3">
          <p className="font-medium text-sm">Words</p>

          {words.map((w, i) => (
            <div key={i} className="flex gap-3">
              <StringField
                value={w.word}
                placeholder="Word"
                onChange={v => {
                  const next = [...words];
                  next[i] = { ...next[i], word: v };
                  updateField("words", next);
                }}
              />

              <select
                value={w.category}
                onChange={e => {
                  const next = [...words];
                  next[i] = {
                    ...next[i],
                    category: e.target.value,
                  };
                  updateField("words", next);
                }}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">Category</option>
                {categories.filter(Boolean).map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateField(
                    "words",
                    words.filter((_, idx) => idx !== i)
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}

          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              updateField("words", [
                ...words,
                { word: "", category: "" },
              ])
            }
          >
            Add Word
          </Button>
        </div>
      </div>
    );
  }

  /* ================= PREVIEW MODE ================= */
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-base">
          Categorize the Words
        </CardTitle>
        <CardDescription>
          Drag each word into the correct category.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* Word Bank */}
        <div className="rounded-xl border bg-muted/40 p-4">
          <div className="flex flex-wrap justify-center gap-3 min-h-[72px]">
            {wordBank.length ? (
              wordBank.map(word => (
                <Button
                  key={word}
                  draggable={!showResults}
                  variant="secondary"
                  size="sm"
                  className="cursor-grab"
                  onDragStart={e =>
                    e.dataTransfer.setData(
                      "text/plain",
                      word
                    )
                  }
                >
                  {word}
                </Button>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                All words placed
              </p>
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="grid md:grid-cols-2 gap-6">
          {categories.map(cat => (
            <div
              key={cat}
              onDrop={e => handleDrop(e, cat)}
              onDragOver={e => e.preventDefault()}
              className="rounded-xl border-2 border-dashed bg-background p-5 min-h-[180px] transition-colors hover:border-primary/60"
            >
              <h3 className="text-center font-semibold mb-4">
                {cat}
              </h3>

              <div className="space-y-2">
                {Object.entries(answers)
                  .filter(([, c]) => c === cat)
                  .map(([word]) => {
                    const correct =
                      words.find(w => w.word === word)
                        ?.category === cat;

                    return (
                      <div
                        key={word}
                      className={cn(
                          "p-3 rounded transition text-sm text-center font-medium border",
                          showResults && correct && "bg-green-100 border-green-300",
                          showResults && !correct && "bg-red-100 border-red-300",
                          !showResults && "bg-muted border-transparent"
                        )}
                      >
                        {word}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
